<script lang="ts">
  // Virtualised message list (spec §9). Only the rows inside the viewport plus 6 rows of
  // overscan exist in the DOM; the window is a single `$derived` over scrollTop / viewport
  // height, and the container is measured once per resize through a ResizeObserver.
  import { datePeriod, t } from '$lib/i18n';
  import { displayName, pathContext } from '$lib/mail/folders';
  import { store } from '$lib/store';
  import type { ThreadRow } from '$lib/types';
  import { isFinePointer, isMobile, tooltip } from '$lib/util/dom';
  import DateSep from './DateSep.svelte';
  import EmptyState, { type EmptySpec } from './EmptyState.svelte';
  import QueryChips from './QueryChips.svelte';
  import QuickChips from './QuickChips.svelte';
  import Row from './Row.svelte';
  import Skeleton from './Skeleton.svelte';
  import { PTR_SPINNER_H, PTR_TRIGGER, pullToRefresh } from './pullToRefresh';

  let { oncontext }: { oncontext?: (ev: MouseEvent, row: ThreadRow) => void } = $props();

  const OVERSCAN = 6;
  const LOAD_MORE_PX = 300;
  const DEFAULT_VIEWPORT = 600;

  let listEl = $state<HTMLDivElement | null>(null);
  let spacerEl = $state<HTMLDivElement | null>(null);
  let scrollTop = $state(0);
  let viewportH = $state(DEFAULT_VIEWPORT);
  let spacerTop = $state(0);
  let pullDy = $state(0);
  let refreshing = $state(false);
  let finePointer = $state(isFinePointer());
  let mobile = $state(isMobile());

  const rows = $derived(store.rows);
  const count = $derived(rows.length);
  const rowH = $derived(store.density === 'compact' ? 56 : 76);
  const searching = $derived(store.searchResults !== null);
  const folderRec = $derived(store.folders.find((f) => f.path === store.currentFolder) ?? null);
  const folderLabel = $derived(folderRec ? displayName(folderRec, pathContext(store.folders)) : store.currentFolder);
  const cursorIndex = $derived(store.cursorKey ? rows.findIndex((r) => r.key === store.cursorKey) : -1);
  const activeId = $derived(cursorIndex >= 0 ? `r-${rows[cursorIndex]!.uid}` : undefined);

  /** The visible window: [start, end) with overscan, from the scroll offset inside the spacer. */
  const win = $derived.by(() => {
    if (!count) return { start: 0, end: 0 };
    const offset = Math.max(0, scrollTop - spacerTop);
    const first = Math.floor(offset / rowH);
    const last = Math.ceil((offset + viewportH) / rowH);
    return { start: Math.max(0, first - OVERSCAN), end: Math.min(count, last + OVERSCAN) };
  });
  const visible = $derived(rows.slice(win.start, win.end));

  const chipsVisible = $derived.by(() => {
    const q = store.searchQ.trim();
    if (store.followupOn) return false;
    if (!q) return true;
    return ['is:unread', 'has:attachment', 'is:starred', 'is:personal', 'is:list', `to:${store.user ?? ''}`].includes(q);
  });
  const dateSep = $derived.by(() => {
    if (store.sortMode !== 'date' || searching || !count) return null;
    const first = rows[Math.min(count - 1, Math.max(0, Math.floor(Math.max(0, scrollTop - spacerTop) / rowH)))];
    return first ? t(datePeriod(first.receivedAt ?? first.date)) : null;
  });
  const showSkeleton = $derived(count === 0 && (store.syncing || store.searching));
  const shownTail = $derived.by(() => {
    if (searching) return store.searchMatched > count ? { kind: 'search' as const, n: store.searchMatched, k: count } : null;
    if (store.folderTotal > store.folderLoaded) return { kind: 'more' as const, n: store.folderLoaded, m: store.folderTotal };
    return null;
  });

  const empty = $derived.by<EmptySpec | null>(() => {
    if (count > 0 || showSkeleton) return null;
    if (store.searchError) return { kind: 'search-error', title: t('searchFailed'), hint: t('searchFailedHint'), action: { label: t('retry'), run: () => store.retrySearch() } };
    if (searching) return { kind: 'no-results', title: t('noResultsTitle'), hint: t('noResultsHint', { q: store.searchQ.trim(), f: folderRec?.name ?? store.currentFolder }), action: { label: t('clearSearchBtn'), run: () => store.clearSearch() } };
    if (store.offline && store.folderTotal === 0) return { kind: 'offline', title: t('offlineEmpty'), hint: t('offlineEmptyHint') };
    if (store.lastSyncError?.folder === store.currentFolder || ((folderRec?.messages ?? 0) > 0 && !store.syncing)) {
      return { kind: 'error', title: t('syncFailed'), hint: t('syncFailedHint'), action: { label: t('retry'), run: () => void store.retrySync() } };
    }
    const su = folderRec?.specialUse ?? null;
    switch (su) {
      case '\\Inbox':
        return { kind: 'empty', title: t('emptyInbox'), hint: t('emptyInboxHint') };
      case '\\Trash':
        return { kind: 'empty', title: t('emptyTrash') };
      case '\\Drafts':
        return { kind: 'empty', title: t('emptyDrafts'), hint: t('emptyDraftsHint'), action: { label: t('compose'), run: () => store.openCompose() } };
      case '\\Sent':
        return { kind: 'empty', title: t('emptySent') };
      case '\\Junk':
        return { kind: 'empty', title: t('emptyJunk') };
      default:
        return { kind: 'empty', title: su ? t('emptyGeneric') : t('emptyFolder') };
    }
  });

  function measure() {
    const el = listEl;
    if (!el) return;
    const h = el.clientHeight;
    viewportH = h > 0 ? h : DEFAULT_VIEWPORT;
    spacerTop = spacerEl?.offsetTop ?? 0;
  }

  function onScroll() {
    const el = listEl;
    if (!el) return;
    scrollTop = el.scrollTop;
    store.listScrollTop = el.scrollTop;
    spacerTop = spacerEl?.offsetTop ?? 0;
    if (!searching && el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_PX) void store.loadMore();
  }

  // Measure once per resize; restore the remembered scroll position on mount.
  $effect(() => {
    const el = listEl;
    if (!el) return;
    measure();
    if (store.listScrollTop) {
      el.scrollTop = store.listScrollTop;
      scrollTop = el.scrollTop;
    }
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  });

  // Previews for the visible rows that lack a snippet (one effect over the window, not per row).
  $effect(() => {
    if (searching) return;
    const need = visible.filter((r) => !r.snippet).map((r) => r.uid);
    if (need.length) store.ensurePreviews(need);
  });

  // Keep the cursor row in view with one row of margin.
  $effect(() => {
    const i = cursorIndex;
    const el = listEl;
    if (i < 0 || !el) return;
    const top = spacerTop + i * rowH;
    const bottom = top + rowH;
    const viewTop = el.scrollTop;
    const viewBottom = viewTop + viewportH;
    if (top - rowH < viewTop) el.scrollTop = Math.max(0, top - rowH);
    else if (bottom + rowH > viewBottom) el.scrollTop = bottom + rowH - viewportH;
  });

  // Fine-pointer / mobile media listeners.
  $effect(() => {
    if (typeof matchMedia === 'undefined') return;
    const fp = matchMedia('(hover: hover) and (pointer: fine)');
    const mb = matchMedia('(max-width: 900px)');
    const onFp = () => (finePointer = fp.matches);
    const onMb = () => (mobile = mb.matches);
    fp.addEventListener?.('change', onFp);
    mb.addEventListener?.('change', onMb);
    return () => {
      fp.removeEventListener?.('change', onFp);
      mb.removeEventListener?.('change', onMb);
    };
  });

  async function onPullRelease(h: number) {
    if (h > PTR_TRIGGER && !refreshing) {
      refreshing = true;
      pullDy = PTR_SPINNER_H;
      try {
        await store.refresh();
      } finally {
        refreshing = false;
        pullDy = 0;
      }
    } else pullDy = 0;
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div
  class="list"
  role="grid"
  tabindex="0"
  aria-multiselectable="true"
  aria-rowcount={count}
  aria-activedescendant={activeId}
  aria-label={folderLabel}
  data-density={store.density}
  style="--rows:{count};--row-h:{rowH}px"
  bind:this={listEl}
  onscroll={onScroll}
  use:pullToRefresh={{ enabled: () => mobile, onPull: (h) => (pullDy = h), onRelease: (h) => void onPullRelease(h) }}
>
  {#if dateSep}<DateSep label={dateSep} />{/if}
  {#if pullDy > 0 || refreshing}
    <div class="ptr" aria-label={t('pullRefresh')} style="height:{pullDy}px"><span class="ptr-spin" class:go={refreshing}></span></div>
  {/if}
  {#if chipsVisible}<QuickChips />{/if}
  {#if store.searchChips.length}<QueryChips />{/if}
  {#if searching && store.searchMatched > 0 && !store.searchError}
    <div class="allbar" role="status">
      <span>{t('actAllMatches', { n: store.searchMatched })}</span>
      <button type="button" class="allbtn" onclick={() => void store.actOnAllMatches('seen')}>{t('bulkMarkRead')}</button>
    </div>
  {/if}
  {#if store.searchError && count > 0}
    <div class="searchfail" role="status" data-kind="search-error">
      <span>{t('searchFailedPartial')}</span>
      <button type="button" class="sf-retry" onclick={() => store.retrySearch()}>{t('retry')}</button>
    </div>
  {/if}
  {#if showSkeleton}
    <Skeleton />
  {:else if empty}
    <EmptyState spec={empty} />
  {:else}
    <div class="spacer" role="rowgroup" data-loaded={store.folderLoaded} data-total={store.folderTotal} data-msgcount={count} style="height:{count * rowH}px" bind:this={spacerEl}>
      {#each visible as row, i (row.key)}
        <Row {row} index={win.start + i} top={(win.start + i) * rowH} cursor={row.key === store.cursorKey} {finePointer} {mobile} {oncontext} />
      {/each}
    </div>
    {#if shownTail?.kind === 'search'}
      <div class="tail"><span class="list-count">{t('matchedN', { n: shownTail.n, k: shownTail.k })}</span></div>
    {:else if shownTail?.kind === 'more'}
      <div class="tail">
        <button type="button" class="more-btn" use:tooltip onclick={() => void store.loadMore()}>{t('loadMore')}</button>
        <span class="list-count" data-loaded={shownTail.n} data-total={shownTail.m}>{t('countOf', { n: shownTail.n, m: shownTail.m })}</span>
      </div>
    {/if}
  {/if}
</div>

<style>
  /* @css list/MessageList start */
  .list {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    background: var(--surface-page)
  }

  .spacer {
    position: relative
  }

  .allbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin: var(--space-2) var(--space-3) 0;
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-accent);
    border-radius: var(--radius-md);
    background: var(--surface-subtle);
    font-size: var(--text-sm)
  }

  .allbtn {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-body);
    padding: 2px var(--space-3);
    font: inherit;
    cursor: pointer;
    min-height: var(--control-sm)
  }

  .allbtn:active {
    background: var(--mail-pressed)
  }

  .searchfail {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin: var(--space-2) var(--space-3);
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--border-warning, var(--border-default));
    border-radius: var(--radius-md);
    background: var(--surface-warning-subtle, var(--surface-sunken));
    color: var(--text-body);
    font-size: var(--text-sm);
    line-height: var(--leading-body)
  }

  .sf-retry {
    flex: none;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-body);
    padding: var(--space-1) var(--space-3);
    font: inherit;
    cursor: pointer;
    min-height: 32px
  }

  .tail {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
    padding: var(--space-4);
    padding-bottom: var(--space-8)
  }

  .more-btn {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    padding: 8px var(--space-5);
    min-block-size: var(--control-md);
    color: var(--text-body);
    font-size: var(--text-sm)
  }

  .more-btn:hover {
    background: var(--surface-subtle)
  }

  .more-btn:active {
    background: var(--mail-pressed)
  }

  .list-count {
    color: var(--text-faint);
    font-size: var(--text-xs);
    font-variant-numeric: tabular-nums
  }

  .ptr {
    display: grid;
    place-items: center;
    overflow: hidden
  }

  .ptr-spin {
    width: 24px;
    height: 24px;
    border: 2px solid var(--border-default);
    border-top-color: var(--blue-500);
    border-radius: var(--radius-pill)
  }

  .ptr-spin.go {
    animation: svelte-kpj0wp-rot .8s linear infinite
  }

  @media (pointer:fine) {
  .list {
      scrollbar-gutter: stable
    }
  }

  .list::before {
    content: "";
    display: block;
    block-size: var(--datesep-h)
  }

  .allbar,
  .searchfail {
    flex-wrap: wrap
  }

  .allbar > span,
  .searchfail > span {
    min-width: 0;
    flex: 1 1 12ch
  }

  @media (max-width:900px) {
  .list {
      padding-bottom: var(--space-6)
    }
  }

  @keyframes svelte-kpj0wp-rot {
    to {
      transform: rotate(360deg)
    }
  }
  /* @css list/MessageList end */
</style>
